import React, { useState, useEffect, useCallback } from 'react';
import { FaUserPlus, FaEdit, FaTrash, FaSearch, FaShieldAlt, FaUser, FaTimes, FaSave, FaKey, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useModal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useApiCall } from '../hooks/useApiCall';
import SortTh from '../components/SortTh';
import '../styles/Users.css';

const PERMISSIONS = [
  { key: 'dashboard',    label: 'Dashboard' },
  { key: 'fuel_levels',  label: 'Fuel Levels' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'tankers',      label: 'Tankers' },
  { key: 'live_fueling', label: 'Live Fueling' },
  { key: 'statistics',   label: 'Statistics' },
  { key: 'reference',    label: 'Reference Data' },
  { key: 'users',        label: 'Users' },
];

function UserForm({ user, onSave, closeModal }) {
  const [form, setForm] = useState(user
    ? { name: user.name, email: user.email, phone: user.phone || '', role: user.role, status: user.status, password: '', permissions: user.permissions || {} }
    : { name: '', email: '', phone: '', role: 'fueler', status: 'active', password: '', permissions: {} }
  );
  const [showPw, setShowPw] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const togglePerm = (k) => set('permissions', { ...form.permissions, [k]: !form.permissions[k] });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...form, id: user?.id || 0 });
    closeModal();
  };

  return (
    <form id="user-form" onSubmit={handleSubmit} className="user-form">
      <div className="form-row">
        <label className="form-label">Full name</label>
        <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Carlos Hew" />
      </div>
      <div className="form-row">
        <label className="form-label">Email</label>
        <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required placeholder="e.g. carlos@uas.sr" />
      </div>
      <div className="form-row">
        <label className="form-label">Phone</label>
        <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+597 ..." />
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Role</label>
          <select className="form-input" value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="fueler">Fueler</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Status</label>
          <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {form.role !== 'admin' && (
        <div className="form-row">
          <label className="form-label"><FaKey style={{ marginRight: 4, fontSize: 11 }} />Page Access</label>
          <div className="permissions-grid">
            {PERMISSIONS.map(({ key, label }) => (
              <label key={key} className={`perm-toggle ${form.permissions[key] ? 'on' : ''}`}>
                <input type="checkbox" checked={!!form.permissions[key]} onChange={() => togglePerm(key)} style={{ display: 'none' }} />
                {label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#8896a5', marginTop: 6 }}>Select which tabs this user can access.</div>
        </div>
      )}
      {form.role === 'admin' && (
        <div className="perm-admin-note"><FaShieldAlt /> Admin role grants full access to all pages automatically.</div>
      )}

      <div className="form-row">
        <label className="form-label">{user ? 'New password (leave blank to keep)' : 'Password'}</label>
        <div className="pw-input-wrap">
          <input className="form-input" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} required={!user} placeholder={user ? 'Leave blank to keep current' : 'Set initial password'} />
          <button type="button" className="pw-eye-btn" onClick={() => setShowPw(v => !v)}>
            {showPw ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>
    </form>
  );
}

function DeleteConfirm({ user, onDelete, closeModal }) {
  return (
    <div className="delete-confirmation">
      <p>Are you sure you want to delete <strong>{user.name}</strong>?</p>
      <p className="warning-text">This action cannot be undone.</p>
    </div>
  );
}

export default function Users() {
  const apiCall = useApiCall();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy,  setSortBy]  = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const { openModal } = useModal();
  const { success, showToast } = useToast();

  const load = useCallback((p, q, sb = 'id', sd = 'desc') => {
    setLoading(true);
    apiCall('admin/users', 'POST', { page: p, query: q, sort_by: sb, sort_dir: sd }).then(res => {
      if (res?.success) {
        setUsers(res.data.users || []);
        setTotal(res.data.total || 0);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(1, ''); }, []);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    setPage(1);
    load(1, q, sortBy, sortDir);
  };

  const handlePage = (p) => {
    setPage(p);
    load(p, search, sortBy, sortDir);
  };

  const handleSort = (col) => {
    const dir = sortBy === col && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(col);
    setSortDir(dir);
    setPage(1);
    load(1, search, col, dir);
  };

  const handleSave = (form) => {
    apiCall('admin/users/update', 'POST', { form }).then(res => {
      if (res?.success) {
        success(form.id > 0 ? `${form.name} updated` : `${form.name} created`);
        load(page, search);
      } else {
        showToast(res?.response || 'Failed to save user', 'error');
      }
    });
  };

  const handleDelete = (id) => {
    apiCall('admin/users/delete', 'POST', { id }).then(res => {
      if (res?.success) {
        success('User deleted');
        load(page, search);
      } else {
        showToast(res?.response || 'Failed to delete user', 'error');
      }
    });
  };

  const totalPages = Math.ceil(total / 20);

  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString() : '-';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p className="page-subtitle">Manage fueler accounts and admin access</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal('Create user', <UserForm onSave={handleSave} />, 'sm', {
          closeOnBackdrop: false,
          footer: (closeModal) => (
            <>
              <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
              <button type="submit" form="user-form" className="btn btn-primary"><FaSave /> Create user</button>
            </>
          )
        })}>
          <FaUserPlus /> Create user
        </button>
      </div>

      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search by name or email..."
          className="search-input"
          value={search}
          onChange={handleSearch}
        />
      </div>

      <div className="users-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <SortTh col="role"      label="Role"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <th>Access</th>
              <SortTh col="status"    label="Status"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="timestamp" label="Created" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="table-empty">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={8} className="table-empty">No users found.</td></tr>
            ) : users.map(u => (
              <tr key={u.id}>
                <td>
                  <div className="user-cell">
                    <div className="user-avatar-sm">{u.name.charAt(0)}</div>
                    {u.name}
                  </div>
                </td>
                <td>{u.email}</td>
                <td>{u.phone || '-'}</td>
                <td>
                  <span className={`role-badge ${u.role}`}>
                    {u.role === 'admin' ? <FaShieldAlt /> : <FaUser />} {u.role}
                  </span>
                </td>
                <td>
                  {u.role === 'admin' ? (
                    <span style={{ fontSize: 11, color: '#35c068' }}>All pages</span>
                  ) : u.permissions ? (
                    <div className="perm-badges">
                      {PERMISSIONS.filter(p => u.permissions[p.key]).map(p => (
                        <span key={p.key} className="perm-badge">{p.label}</span>
                      ))}
                      {PERMISSIONS.every(p => !u.permissions[p.key]) && <span style={{ fontSize: 11, color: '#8896a5' }}>No access</span>}
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: '#8896a5' }}>No access</span>
                  )}
                </td>
                <td><span className={`status-dot ${u.status}`}>{u.status}</span></td>
                <td>{fmtDate(u.timestamp)}</td>
                <td>
                  <div className="table-actions">
                    <button className="btn btn-sm btn-secondary" onClick={() => openModal(`Edit - ${u.name}`, <UserForm user={u} onSave={handleSave} />, 'sm', {
                      closeOnBackdrop: false,
                      footer: (closeModal) => (
                        <>
                          <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
                          <button type="submit" form="user-form" className="btn btn-primary"><FaSave /> Save changes</button>
                        </>
                      )
                    })}><FaEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => openModal('Delete user', <DeleteConfirm user={u} onDelete={handleDelete} />, 'sm', {
                      footer: (closeModal) => (
                        <>
                          <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                          <button className="btn btn-danger" onClick={() => { handleDelete(u.id); closeModal(); }}>Delete</button>
                        </>
                      )
                    })}><FaTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-row">
          <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => handlePage(page - 1)}>Prev</button>
          <span>Page {page} of {totalPages} &nbsp;({total} total)</span>
          <button className="btn btn-sm btn-secondary" disabled={page >= totalPages} onClick={() => handlePage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
